import { useState, useEffect, useRef, DragEvent } from 'react';
import * as Sentry from '@sentry/react';
import Icon, { IconType } from 'components/Icon';
import FontInput from 'components/Widget/Input/FontInput';
import InlineRenamableTitle from 'components/Widget/InlineRenamableTitle';
import { Attachment, DesignSystemFont, GoogleFont } from 'dragonmouse-shared/interfaces';
import forge from 'apis/forge';
import {
	getAvailableWeights,
	getFallbackOptions,
	getFormatFromFile,
	inferFontNameFromFilename,
	isValidFontFile,
} from 'font';
import { uploadFile, UploadTracker } from 'utils';
import * as TS from '../tokens/TypographySection/styles';
import * as S from './fontListStyles';

interface FontListProps {
	fonts: DesignSystemFont[];
	designSystemId: string;
	previewText: string;
	dark: boolean;
	onCommit: (fonts: DesignSystemFont[]) => void;
}

const CHARSET_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CHARSET_LOWER = 'abcdefghijklmnopqrstuvwxyz';
const CHARSET_NUMS = '0123456789';

const FontList = ({ fonts, designSystemId, previewText, dark, onCommit }: FontListProps) => {
	const [googleFonts, setGoogleFonts] = useState<GoogleFont[]>([]);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [creatingIndex, setCreatingIndex] = useState<number | null>(null);
	const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadTracker>>({});
	const [isAddDragging, setIsAddDragging] = useState(false);
	const addDragCounterRef = useRef(0);
	const addFileInputRef = useRef<HTMLInputElement>(null);
	// keeps async upload callbacks from seeing stale fonts after rapid edits
	const fontsRef = useRef(fonts);
	fontsRef.current = fonts;

	useEffect(() => {
		(async () => {
			try {
				setGoogleFonts(await forge.getGoogleFonts());
			} catch (err) {
				Sentry.captureException(err);
			}
		})();
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDelete = (index: number) => {
		if (fonts.length <= 1) return;
		onCommit(fonts.filter((_, i) => i !== index));
	};

	const onStartReplace = (index: number) => {
		setEditingIndex(index);
	};

	const onCancelEdit = () => {
		setEditingIndex(null);
		// if cancelling a newly created (empty-named) entry, drop it
		if (creatingIndex !== null) {
			onCommit(fonts.filter((_, i) => i !== creatingIndex));
			setCreatingIndex(null);
		}
	};

	const onCommitFamily = (index: number, family: string) => {
		if (!family) {
			onCancelEdit();
			return;
		}
		const availableWeights = getAvailableWeights(family, googleFonts);
		const currentWeights = fonts[index].fontWeight || ['400'];
		const validWeights = currentWeights.filter((w) => availableWeights.includes(w));
		const fallbackOpts = getFallbackOptions(family, googleFonts);
		const next = [...fonts];
		next[index] = {
			...next[index],
			fontFamily: family,
			fontWeight: validWeights.length > 0 ? validWeights : ['400'],
			fallback: fallbackOpts[0]?.value ?? 'sans-serif',
		};
		setEditingIndex(null);
		setCreatingIndex(null);
		onCommit(next);
	};

	const onCommitRename = (index: number, name: string) => {
		const trimmed = name.trim();
		if (!trimmed || fonts[index].fontFamily === trimmed) {
			return;
		}
		const next = [...fonts];
		next[index] = { ...next[index], fontFamily: trimmed };
		onCommit(next);
	};

	const onWeightToggle = (index: number, weight: string) => {
		const current = fonts[index].fontWeight || ['400'];
		const next = [...fonts];
		if (current.includes(weight)) {
			if (current.length > 1) {
				next[index] = { ...next[index], fontWeight: current.filter((w) => w !== weight) };
			}
		} else {
			next[index] = { ...next[index], fontWeight: [...current, weight].sort() };
		}
		onCommit(next);
	};

	const uploadFontFile = async (
		index: number,
		format: 'woff2' | 'woff',
		file: File
	): Promise<void> => {
		if (!isValidFontFile(file)) return;
		const key = `${index}-${format}`;
		setUploadingFiles((prev) => ({
			...prev,
			[key]: { filename: file.name, contentType: file.type, size: file.size, uniqueId: key },
		}));
		await uploadFile(file, {
			onLoadingStart: () => {},
			onLoadingEnd: () =>
				setUploadingFiles((prev) => {
					const out = { ...prev };
					delete out[key];
					return out;
				}),
			onSuccess: (attachment: Attachment) => {
				const next = [...fontsRef.current];
				const current = next[index];
				if (!current) return;
				const inferredName = inferFontNameFromFilename(file.name);
				const shouldUpdateName =
					current.fontFamily === 'Custom Font' || current.fontFamily === '';
				next[index] = {
					...current,
					fontFamily: shouldUpdateName ? inferredName : current.fontFamily,
					files: { ...current.files, [format]: attachment.storagePath },
				};
				onCommit(next);
			},
			onError: (err) => Sentry.captureException(err),
		});
	};

	const triggerReplaceFile = (index: number) => {
		const el = document.createElement('input');
		el.type = 'file';
		el.accept = '.woff,.woff2';
		el.onchange = (e) => {
			const f = (e.target as HTMLInputElement).files?.[0];
			if (!f) return;
			const fmt = getFormatFromFile(f);
			if (fmt) uploadFontFile(index, fmt, f);
		};
		el.click();
	};

	const onAddGoogleFont = () => {
		const newIndex = fonts.length;
		const next: DesignSystemFont[] = [
			...fonts,
			{
				fontFamily: '',
				source: 'google',
				fontWeight: ['400', '700'],
				fallback: 'sans-serif',
			},
		];
		setCreatingIndex(newIndex);
		setEditingIndex(newIndex);
		onCommit(next);
	};

	const processDroppedFiles = async (files: File[]) => {
		const valid = files.filter(isValidFontFile);
		if (valid.length === 0) return;
		const newIndex = fonts.length;
		const newFont: DesignSystemFont = {
			fontFamily: '',
			source: 'custom',
			fallback: 'sans-serif',
			files: {},
		};
		onCommit([...fonts, newFont]);
		for (const file of valid) {
			const fmt = getFormatFromFile(file);
			if (fmt) await uploadFontFile(newIndex, fmt, file);
		}
	};

	const onAddDragEnter = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		addDragCounterRef.current += 1;
		setIsAddDragging(true);
	};
	const onAddDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		addDragCounterRef.current = Math.max(0, addDragCounterRef.current - 1);
		if (addDragCounterRef.current === 0) setIsAddDragging(false);
	};
	const onAddDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		addDragCounterRef.current = 0;
		setIsAddDragging(false);
		processDroppedFiles(Array.from(e.dataTransfer.files));
	};
	const onAddBrowse = () => addFileInputRef.current?.click();
	const onAddBrowseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		processDroppedFiles(Array.from(e.target.files || []));
		e.target.value = '';
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderCard = (font: DesignSystemFont, index: number) => {
		const fontStack = `"${font.fontFamily}", ${font.fallback ?? 'sans-serif'}`;
		const weight = font.fontWeight?.[0] ?? '400';
		const availableWeights =
			font.source === 'google' ? getAvailableWeights(font.fontFamily, googleFonts) : [];
		const isEditing = editingIndex === index;
		const isUploading =
			!!uploadingFiles[`${index}-woff2`] || !!uploadingFiles[`${index}-woff`];

		return (
			<TS.FontCard key={`${font.fontFamily}-${index}`}>
				<TS.FontCardHeader $dark={dark}>
					<S.HeaderRow>
						<TS.FontCardName>
							{isEditing && font.source === 'google' ? (
								<S.NameInputSlot>
									<FontInput
										font={font.fontFamily}
										onFontChange={() => {}}
										onFontCommit={(family) => onCommitFamily(index, family)}
										onCancel={onCancelEdit}
										autoFocus
									/>
								</S.NameInputSlot>
							) : (
								<>
									<InlineRenamableTitle
										variant="fontCard"
										value={font.fontFamily || (isUploading ? 'Uploading…' : 'Unnamed font')}
										onCommit={(trimmed) => onCommitRename(index, trimmed)}
									/>
									<TS.SourceBadge>{font.source}</TS.SourceBadge>
								</>
							)}
						</TS.FontCardName>
						{!isEditing && (
							<S.ActionRow>
								<S.IconButton
									type="button"
									onClick={() =>
										font.source === 'custom'
											? triggerReplaceFile(index)
											: onStartReplace(index)
									}
									title={font.source === 'custom' ? 'Replace file' : 'Change Google font'}
								>
									<Icon type={IconType.REFRESH} width={14} height={14} />
								</S.IconButton>
								<S.IconButton
									type="button"
									onClick={() => onDelete(index)}
									disabled={fonts.length <= 1}
									title={fonts.length <= 1 ? 'At least one font required' : 'Delete'}
								>
									<Icon type={IconType.TRASH} width={14} height={14} />
								</S.IconButton>
							</S.ActionRow>
						)}
					</S.HeaderRow>
					{font.fontFamily && (
						<>
							<TS.FontCardPreview
								$dark={dark}
								style={{ fontFamily: fontStack, fontWeight: Number(weight) }}
							>
								{previewText}
							</TS.FontCardPreview>
							<TS.FontCardCharset
								$dark={dark}
								style={{ fontFamily: fontStack, fontWeight: Number(weight) }}
							>
								{CHARSET_UPPER}
								<br />
								{CHARSET_LOWER}
								<br />
								{CHARSET_NUMS}
							</TS.FontCardCharset>
						</>
					)}
				</TS.FontCardHeader>
				{font.source === 'google' && availableWeights.length > 0 && (
					<TS.FontCardMeta>
						{availableWeights.map((w) => (
							<TS.WeightPill
								key={w}
								type="button"
								$active={font.fontWeight?.includes(w) ?? false}
								onClick={() => onWeightToggle(index, w)}
							>
								{w}
							</TS.WeightPill>
						))}
					</TS.FontCardMeta>
				)}
			</TS.FontCard>
		);
	};

	const renderAddCard = () => (
		<S.AddCard
			$isDragging={isAddDragging}
			onDragEnter={onAddDragEnter}
			onDragLeave={onAddDragLeave}
			onDragOver={(e) => e.preventDefault()}
			onDrop={onAddDrop}
		>
			<S.AddCardBody>
				<S.AddRow onClick={onAddGoogleFont}>
					<Icon type={IconType.GOOGLE} width={20} height={20} />
					<S.AddRowLabel>Add Google Font</S.AddRowLabel>
					{googleFonts.length > 0 && (
						<S.AddRowSubtitle>
							— {googleFonts.length.toLocaleString()} available
						</S.AddRowSubtitle>
					)}
				</S.AddRow>
				<S.AddDivider />
				<S.AddRow onClick={onAddBrowse}>
					<Icon type={IconType.UPLOAD} width={20} height={20} />
					<S.AddRowLabel>Drop font files or click to browse</S.AddRowLabel>
					<S.AddRowSubtitle>.woff2 or .woff</S.AddRowSubtitle>
				</S.AddRow>
			</S.AddCardBody>
			<input
				ref={addFileInputRef}
				type="file"
				accept=".woff,.woff2"
				multiple
				style={{ display: 'none' }}
				onChange={onAddBrowseChange}
			/>
		</S.AddCard>
	);

	// Force-suppress unused-var lint for designSystemId (kept on the API so callers
	// know FontList persists via the DS, even though forge calls happen in the
	// parent's onCommit handler).
	void designSystemId;

	return (
		<TS.FontCardList>
			{fonts.map(renderCard)}
			{renderAddCard()}
		</TS.FontCardList>
	);
};

export default FontList;
